/** Active Calls — live call monitoring with session progress (ringing /
 * connected), per-account and per-node concurrency, and force-release.
 *
 * Data comes from CGRates SessionS via GET /api/v1/active-calls and polls
 * every REFRESH_SECONDS while Live auto-refresh is enabled. Durations tick
 * client-side between polls using the session timestamps. */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  keyframes,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import {
  PhoneCallback as HangupIcon,
  Search as SearchIcon,
  ClearAll as ClearFiltersIcon,
  Storage as NodeIcon,
  Groups as AccountsIcon,
  ArrowForward as ArrowIcon,
  Memory as MediaIcon,
} from "@mui/icons-material";
import { format } from "date-fns";
import { activeCallsApi, monitoringApi } from "../api/client";
import {
  ActiveCall,
  ActiveCallsResponse,
  AccountSummary,
  MediaStatus,
  ServerConcurrency,
} from "../types/activeCall";
import { formatDuration } from "../utils/format";
import PageHeader from "../components/common/PageHeader";
import LiveIndicator from "../components/navigation/LiveIndicator";
import ConfirmDialog from "../components/common/ConfirmDialog";
import { useNotify } from "../components/common/NotificationProvider";
import { useAutoRefresh } from "../hooks/useAutoRefresh";
import { useRefresh } from "../components/layout/RefreshContext";

/** Poll interval for live data (seconds). */
const REFRESH_SECONDS = 5;

/** Client-side tick so durations advance smoothly between polls. */
const TICK_MS = 1000;

/** Pulse animation for the "ringing" state chip. */
const ringPulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.45; }
`;

/** Live elapsed seconds for a call, computed from its timestamps. */
function liveElapsed(call: ActiveCall, nowMs: number): number {
  if (call.state === "connected" && call.answer_time) {
    return Math.max(0, (nowMs - new Date(call.answer_time).getTime()) / 1000);
  }
  if (call.setup_time) {
    return Math.max(0, (nowMs - new Date(call.setup_time).getTime()) / 1000);
  }
  return call.state === "connected" ? call.duration_seconds : call.ringing_seconds;
}

/** State chip: connected = steady green, ringing = pulsing amber. */
function StateChip({ state }: { state: ActiveCall["state"] }) {
  return state === "connected" ? (
    <Chip label="Connected" size="small" color="success" variant="outlined" />
  ) : (
    <Chip
      label="Ringing"
      size="small"
      color="warning"
      variant="outlined"
      sx={{ animation: `${ringPulse} 1.2s ease-in-out infinite` }}
    />
  );
}

/** Node status → chip color. */
function nodeStatusColor(status: string): "success" | "warning" | "error" | "default" {
  if (status === "ONLINE") return "success";
  if (status === "DEGRADED" || status === "DRAINING") return "warning";
  if (status === "DOWN" || status === "OFFLINE") return "error";
  return "default";
}

/** Utilization bar color. */
function utilColor(util: number): "success" | "warning" | "error" {
  if (util >= 0.9) return "error";
  if (util >= 0.7) return "warning";
  return "success";
}

/** Small stat card. */
function StatCard({
  label,
  value,
  sub,
  color = "primary",
}: {
  label: string;
  value: string;
  sub?: string;
  color?: "primary" | "success" | "warning";
}) {
  return (
    <Card sx={{ height: "100%" }}>
      <CardContent sx={{ pb: "16px !important" }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
          {label}
        </Typography>
        <Typography variant="h4" sx={{ mt: 0.5, color: `${color}.main`, fontWeight: 600 }}>
          {value}
        </Typography>
        {sub && (
          <Typography variant="caption" color="text.secondary">
            {sub}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

/** Media node concurrency panel. */
function NodesPanel({ servers }: { servers: ServerConcurrency[] }) {
  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <NodeIcon fontSize="small" color="action" />
        <Typography variant="subtitle2">Media Nodes</Typography>
      </Stack>
      {servers.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No servers registered
        </Typography>
      )}
      <Stack spacing={1.5}>
        {servers.map((s) => (
          <Box key={`${s.server_type}:${s.hostname}`}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  label={s.server_type === "freeswitch" ? "FS" : s.server_type === "kamailio" ? "KAM" : "BILL"}
                  size="small"
                  sx={{ height: 20, fontSize: "0.65rem", fontWeight: 700 }}
                />
                <Typography variant="body2" noWrap sx={{ maxWidth: 160 }}>
                  {s.hostname}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip label={s.status} size="small" color={nodeStatusColor(s.status)} sx={{ height: 20, fontSize: "0.65rem" }} />
                <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums" }}>
                  {s.active_calls.toLocaleString()} / {s.capacity_cc.toLocaleString()}
                </Typography>
              </Stack>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, s.utilization * 100)}
              color={utilColor(s.utilization)}
              sx={{ height: 6, borderRadius: 3 }}
            />
          </Box>
        ))}
      </Stack>
    </Paper>
  );
}

/** Media & transcode panel — live transcode sessions + FS/host resources. */
function MediaPanel({ media }: { media: MediaStatus | null }) {
  if (!media) {
    return (
      <Paper sx={{ p: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <MediaIcon fontSize="small" color="action" />
          <Typography variant="subtitle2">Media & Transcode</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary">Loading media status…</Typography>
      </Paper>
    );
  }
  const fs = media.fs;
  const ch = media.channels;
  const sys = media.system;
  const cpu = fs.cpu_percent ?? 0;
  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <MediaIcon fontSize="small" color="action" />
          <Typography variant="subtitle2">Media & Transcode</Typography>
        </Stack>
        <Chip
          label={fs.available ? "FS UP" : "FS DOWN"}
          size="small"
          color={fs.available ? "success" : "error"}
          sx={{ height: 20, fontSize: "0.65rem", fontWeight: 700 }}
        />
      </Stack>

      {/* Headline transcode numbers */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
        <Box>
          <Typography variant="h4" sx={ch.transcoding_calls > 0 ? { color: "warning.main", fontWeight: 600 } : { fontWeight: 600 }}>
            {ch.transcoding_calls.toLocaleString()}
          </Typography>
          <Typography variant="caption" color="text.secondary">transcoding calls</Typography>
        </Box>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>{ch.total.toLocaleString()}</Typography>
          <Typography variant="caption" color="text.secondary">channels</Typography>
        </Box>
        <Box>
          <Typography
            variant="h4"
            sx={{ fontWeight: 600, color: cpu >= 70 ? "error.main" : cpu >= 40 ? "warning.main" : "text.primary" }}
          >
            {fs.cpu_percent != null ? `${cpu.toFixed(1)}%` : "—"}
          </Typography>
          <Typography variant="caption" color="text.secondary">FS cpu</Typography>
        </Box>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            {fs.rss_mib != null ? `${Math.round(fs.rss_mib)}M` : "—"}
          </Typography>
          <Typography variant="caption" color="text.secondary">FS rss</Typography>
        </Box>
      </Stack>

      {/* Codec mix — what callers are sending (read side) */}
      {Object.keys(ch.codec_mix).length > 0 && (
        <Stack direction="row" spacing={0.5} sx={{ mb: 1, flexWrap: "wrap", gap: 0.5 }}>
          {Object.entries(ch.codec_mix).map(([codec, n]) => (
            <Tooltip key={codec} title={`${n} channel(s) reading ${codec}`} arrow>
              <Chip
                label={`${codec} ${n}`}
                size="small"
                variant="outlined"
                color={codec === "G729" ? "secondary" : "default"}
                sx={{ height: 20, fontSize: "0.65rem" }}
              />
            </Tooltip>
          ))}
        </Stack>
      )}

      {/* Host resources */}
      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
        load {sys.load_1?.toFixed(2) ?? "—"} · mem {sys.mem_used_mib != null && sys.mem_total_mib != null
          ? `${sys.mem_used_mib}/${sys.mem_total_mib} MiB (${sys.mem_percent ?? "—"}%)`
          : "—"}
        {fs.sessions != null ? ` · ${fs.sessions}/${fs.max_sessions ?? "?"} sessions` : ""}
        {fs.cps != null ? ` · ${fs.cps}/${fs.max_cps ?? "?"} CPS` : ""}
      </Typography>

      {!fs.available && fs.reason && (
        <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>
          {fs.reason}
        </Typography>
      )}

      {/* Per-call transcode table */}
      {ch.calls.length > 0 && (
        <Table size="small" sx={{ mt: 1 }} aria-label="transcoding calls">
          <TableHead>
            <TableRow>
              <TableCell>Call</TableCell>
              <TableCell>Caller → Dest</TableCell>
              <TableCell>Codecs</TableCell>
              <TableCell align="right">Dur</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {ch.calls.map((c) => (
              <TableRow key={c.call_uuid} hover>
                <TableCell>
                  <Typography variant="caption" sx={{ fontFamily: "monospace" }}>{c.call_uuid}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" sx={{ fontFamily: "monospace", fontSize: "0.7rem" }}>
                    {c.caller} → {c.dest}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={c.codecs}
                    size="small"
                    color="warning"
                    variant="outlined"
                    sx={{ height: 18, fontSize: "0.65rem", fontFamily: "monospace" }}
                  />
                </TableCell>
                <TableCell align="right">
                  <Typography variant="caption" sx={{ fontVariantNumeric: "tabular-nums" }}>
                    {formatDuration(c.duration_s)}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Paper>
  );
}

/** Top accounts by live concurrency panel. */
function AccountsPanel({ accounts }: { accounts: AccountSummary[] }) {
  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <AccountsIcon fontSize="small" color="action" />
        <Typography variant="subtitle2">Top Accounts (live)</Typography>
      </Stack>
      {accounts.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No active sessions
        </Typography>
      )}
      <Stack spacing={1}>
        {accounts.map((a) => (
          <Stack key={a.account} direction="row" justifyContent="space-between" alignItems="center">
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                {a.company || a.account}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block", maxWidth: 200 }}>
                {a.account}
              </Typography>
            </Box>
            <Stack direction="row" spacing={0.5}>
              <Tooltip title="Connected" arrow>
                <Chip label={a.connected} size="small" color="success" variant="outlined" sx={{ height: 22 }} />
              </Tooltip>
              <Tooltip title="Ringing" arrow>
                <Chip label={a.ringing} size="small" color="warning" variant="outlined" sx={{ height: 22 }} />
              </Tooltip>
            </Stack>
          </Stack>
        ))}
      </Stack>
    </Paper>
  );
}

/** Active Calls page component. */
export default function ActiveCalls() {
  const notify = useNotify();

  const [data, setData] = useState<ActiveCallsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Live toggle is shared with the shell via RefreshContext so this page's
  // LiveIndicator and the top-bar one drive the same polling state (F3).
  const { autoRefresh, toggleAutoRefresh } = useRefresh();

  // Filters
  const [stateFilter, setStateFilter] = useState("");
  const [accountFilter, setAccountFilter] = useState("");
  const [destinationFilter, setDestinationFilter] = useState("");
  const [supplierFilter, setSupplierFilter] = useState("");
  const [sort, setSort] = useState("newest");

  // Pagination
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  // 1s tick so durations animate between polls
  const [nowTick, setNowTick] = useState(() => Date.now());

  // Terminate dialog state
  const [terminating, setTerminating] = useState<ActiveCall | null>(null);
  const [terminateLoading, setTerminateLoading] = useState(false);
  // Live media/transcode + resource snapshot (GET /monitoring/media-status)
  const [media, setMedia] = useState<MediaStatus | null>(null);

  // Debounce text filters so typing doesn't fire a request per keystroke
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [debouncedFilters, setDebouncedFilters] = useState({ account: "", destination: "", supplier: "" });
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedFilters({
        account: accountFilter,
        destination: destinationFilter,
        supplier: supplierFilter,
      });
      setPage(0);
    }, 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [accountFilter, destinationFilter, supplierFilter]);

  // Guards against stale-response races: only the LATEST fetch may touch
  // state (filter change while a slow poll is in flight used to overwrite
  // the filtered result with the unfiltered one).
  const fetchSeqRef = useRef(0);

  const fetchCalls = useCallback(
    async (silent = false) => {
      const seq = ++fetchSeqRef.current;
      if (silent) setRefreshing(true);
      else setLoading(true);
      try {
        const resp = await activeCallsApi.list({
          state: stateFilter || undefined,
          account: debouncedFilters.account || undefined,
          destination: debouncedFilters.destination || undefined,
          supplier: debouncedFilters.supplier || undefined,
          sort,
          page: page + 1,
          page_size: pageSize,
        });
        if (seq !== fetchSeqRef.current) return; // a newer request superseded this one
        setData(resp.data);
        setError(null);
      } catch (e: unknown) {
        if (seq !== fetchSeqRef.current) return;
        const msg = e instanceof Error ? e.message : "Failed to load active calls";
        setError(msg);
      } finally {
        if (seq === fetchSeqRef.current) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [stateFilter, debouncedFilters, sort, page, pageSize]
  );

  // Initial + dependency-driven load
  useEffect(() => {
    void fetchCalls();
  }, [fetchCalls]);

  // Auto-refresh polling — the hook pauses while the tab is hidden,
  // re-fetches when it becomes visible again, and re-runs on shell manual
  // refresh. Polls stay silent (spinner-only) like the old interval.
  useAutoRefresh(() => fetchCalls(true), REFRESH_SECONDS * 1000);

  // Media/transcode snapshot: initial load + same refresh cadence
  // (also paused while hidden)
  const fetchMedia = useCallback(async () => {
    try {
      const resp = await monitoringApi.getMediaStatus();
      setMedia(resp.data);
    } catch {
      // Keep the last snapshot; the panel shows FS DOWN if it stays empty.
    }
  }, []);
  // Media/transcode snapshot polling — the hook performs the initial fetch
  // on mount, polls while Live auto-refresh is on, and re-fetches on shell
  // manual refresh and tab re-show.
  useAutoRefresh(fetchMedia, REFRESH_SECONDS * 1000);

  // Duration ticker
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), TICK_MS);
    return () => clearInterval(id);
  }, []);

  const handleTerminate = useCallback(async () => {
    if (!terminating) return;
    setTerminateLoading(true);
    try {
      const resp = await activeCallsApi.terminate({
        cgrid: terminating.cgrid,
        tenant: terminating.tenant,
        account: terminating.account,
      });
      const body = resp.data as { status?: string; outcome?: string; partial?: boolean };
      if (body?.status === "already_ended") {
        // Racing hangup — the call went away before the release landed
        notify(`Call ${terminating.cgrid.slice(0, 8)} already ended (no release needed)`, "info");
      } else if (body?.partial) {
        // CGRates deactivated the session but could not confirm RTP teardown
        notify(
          `Call ${terminating.cgrid.slice(0, 8)} released, but RTP teardown unconfirmed — verify media state`,
          "warning"
        );
      } else {
        notify(`Call ${terminating.cgrid.slice(0, 8)} released`);
      }
      setTerminating(null);
      await fetchCalls(true);
    } catch (e: unknown) {
      const detail =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
        (e instanceof Error ? e.message : "Failed to terminate call");
      notify(`Terminate failed: ${detail}`, "error");
    } finally {
      setTerminateLoading(false);
    }
  }, [terminating, notify, fetchCalls]);

  const hasFilters =
    stateFilter !== "" ||
    debouncedFilters.account !== "" ||
    debouncedFilters.destination !== "" ||
    debouncedFilters.supplier !== "";

  const clearFilters = () => {
    setStateFilter("");
    setAccountFilter("");
    setDestinationFilter("");
    setSupplierFilter("");
    setSort("newest");
    setPage(0);
  };

  const summary = data?.summary;
  const calls = data?.calls ?? [];
  const total = data?.total ?? 0;

  const lastUpdated = useMemo(
    () => (data ? format(new Date(data.generated_at), "HH:mm:ss") : "—"),
    [data]
  );

  return (
    <Box>
      <PageHeader
        title="Active Calls"
        subtitle={`Live sessions across all clients and routes · updated ${lastUpdated}`}
        actions={
          <LiveIndicator
            autoRefresh={autoRefresh}
            onToggleRefresh={toggleAutoRefresh}
            onManualRefresh={() => void fetchCalls(true)}
            refreshing={refreshing}
            interval={REFRESH_SECONDS}
          />
        }
      />

      {/* CGRates degraded banner */}
      {data && !data.available && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Live session data unavailable — rating engine could not be reached.
          Node concurrency below is from control-plane inventory.
          {data.error ? ` (${data.error})` : ""}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Summary stat cards */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}>
          <StatCard label="Total Active" value={(summary?.total ?? 0).toLocaleString()} sub="sessions in progress" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="Connected" value={(summary?.connected ?? 0).toLocaleString()} sub="answered, billed" color="success" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label="Ringing"
            value={(summary?.ringing ?? 0).toLocaleString()}
            sub={summary && summary.longest_ringing_seconds > 0 ? `longest ${formatDuration(summary.longest_ringing_seconds)}` : "awaiting answer"}
            color="warning"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label="Longest Call"
            value={formatDuration(summary?.longest_call_seconds ?? 0)}
            sub={`avg age shown per row`}
          />
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>State</InputLabel>
              <Select value={stateFilter} label="State" onChange={(e) => { setStateFilter(e.target.value); setPage(0); }}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="connected">Connected</MenuItem>
                <MenuItem value="ringing">Ringing</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Account / Company"
              value={accountFilter}
              onChange={(e) => setAccountFilter(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Destination prefix"
              value={destinationFilter}
              onChange={(e) => setDestinationFilter(e.target.value)}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Supplier / Route"
              value={supplierFilter}
              onChange={(e) => setSupplierFilter(e.target.value)}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Sort</InputLabel>
              <Select value={sort} label="Sort" onChange={(e) => { setSort(e.target.value); setPage(0); }}>
                <MenuItem value="newest">Newest first</MenuItem>
                <MenuItem value="longest">Longest connected</MenuItem>
                <MenuItem value="ringing">Longest ringing</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <Button
              fullWidth
              size="small"
              variant="outlined"
              startIcon={<ClearFiltersIcon />}
              disabled={!hasFilters}
              onClick={clearFilters}
              sx={{ height: 40 }}
            >
              Clear filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={2}>
        {/* Live calls table */}
        <Grid item xs={12} lg={8}>
          <Paper>
            {refreshing && <LinearProgress sx={{ borderTopLeftRadius: 4, borderTopRightRadius: 4 }} />}
            <TableContainer sx={{ opacity: refreshing ? 0.7 : 1, transition: "opacity 0.2s" }}>
              <Table size="small" aria-label="active calls">
                <TableHead>
                  <TableRow>
                    <TableCell>State</TableCell>
                    <TableCell>Caller → Callee</TableCell>
                    <TableCell>Account</TableCell>
                    <TableCell>Dest</TableCell>
                    <TableCell>Route / Supplier</TableCell>
                    <TableCell>Node</TableCell>
                    <TableCell align="right">Duration</TableCell>
                    <TableCell align="right">Started</TableCell>
                    <TableCell padding="checkbox" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading &&
                    Array.from({ length: 8 }).map((_, i) => (
                      <TableRow key={`sk-${i}`}>
                        {Array.from({ length: 9 }).map((__, j) => (
                          <TableCell key={j}>
                            <Box sx={{ height: 14, borderRadius: 1, bgcolor: (t) => alpha(t.palette.text.primary, 0.08) }} />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}

                  {!loading && calls.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9}>
                        <Box sx={{ textAlign: "center", py: 6 }}>
                          <Typography variant="body1" color="text.secondary">
                            {hasFilters
                              ? "No calls match the current filters"
                              : "No active calls right now"}
                          </Typography>
                          {hasFilters && (
                            <Typography
                              variant="body2"
                              color="primary"
                              sx={{ mt: 1, cursor: "pointer" }}
                              onClick={clearFilters}
                            >
                              Clear filters
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}

                  {!loading &&
                    calls.map((call) => {
                      const elapsed = liveElapsed(call, nowTick);
                      const ringingTooLong = call.state === "ringing" && elapsed > 60;
                      return (
                        <TableRow key={call.cgrid} hover>
                          <TableCell>
                            <StateChip state={call.state} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>
                              {call.caller ?? "—"}
                              <ArrowIcon fontSize="small" sx={{ mx: 0.5, verticalAlign: "middle", color: "text.secondary" }} />
                              {call.callee ?? "—"}
                            </Typography>
                            <Tooltip title={`CGRID ${call.cgrid}${call.origin_id ? ` · FS UUID ${call.origin_id}` : ""}`} arrow>
                              <Typography variant="caption" color="text.secondary" sx={{ cursor: "default" }}>
                                {call.cgrid.slice(0, 8)}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 160 }}>
                              {call.company || call.account || "—"}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block", maxWidth: 160 }}>
                              {call.account || ""}
                              {call.customer_type ? ` · ${call.customer_type}` : ""}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>
                              {call.destination ?? call.callee ?? "—"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 140 }}>
                              {call.supplier || call.route || "—"}
                            </Typography>
                            {call.target && (
                              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block", maxWidth: 140 }}>
                                {call.target}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 100 }}>
                              {call.node || call.origin_host || "—"}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              sx={{
                                fontVariantNumeric: "tabular-nums",
                                color: ringingTooLong ? "warning.main" : "text.primary",
                                fontWeight: ringingTooLong ? 700 : 400,
                              }}
                            >
                              {formatDuration(elapsed)}
                            </Typography>
                            {call.state === "ringing" && (
                              <Typography variant="caption" color="text.secondary">
                                ringing
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums" }}>
                              {call.setup_time ? format(new Date(call.setup_time), "HH:mm:ss") : "—"}
                            </Typography>
                          </TableCell>
                          <TableCell padding="checkbox">
                            <Tooltip title="Force release this call" arrow>
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => setTerminating(call)}
                                  aria-label={`Release call ${call.cgrid}`}
                                >
                                  <HangupIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(e) => {
                setPageSize(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[25, 50, 100, 250]}
            />
          </Paper>
        </Grid>

        {/* Side panels */}
        <Grid item xs={12} md={6} lg={4}>
          <Stack spacing={2}>
            <NodesPanel servers={summary?.servers ?? []} />
            <AccountsPanel accounts={summary?.top_accounts ?? []} />
            <MediaPanel media={media} />
          </Stack>
        </Grid>
      </Grid>

      {/* Terminate confirmation */}
      <ConfirmDialog
        open={terminating !== null}
        title="Force release call"
        message={
          terminating
            ? `Release the call from ${terminating.caller ?? "?"} to ${terminating.callee ?? "?"}?`
            : ""
        }
        details={
          terminating ? (
            <Stack spacing={0.5}>
              <Typography variant="body2">Account: {terminating.company || terminating.account || "—"}</Typography>
              <Typography variant="body2">CGRID: {terminating.cgrid}</Typography>
              <Typography variant="body2">
                {terminating.state === "connected"
                  ? `Connected for ${formatDuration(liveElapsed(terminating, nowTick))}`
                  : `Ringing for ${formatDuration(liveElapsed(terminating, nowTick))}`}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                The session is released by the billing engine; billing stops and a CDR is generated.
              </Typography>
            </Stack>
          ) : null
        }
        confirmLabel="Release call"
        severity="error"
        loading={terminateLoading}
        onConfirm={() => void handleTerminate()}
        onClose={() => setTerminating(null)}
      />
    </Box>
  );
}
